import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Leaf, Users, Award, TrendingUp } from 'lucide-react';

export const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Transparency',
      description: 'No hidden costs, no false promises. We believe in complete honesty with our customers.'
    },
    {
      icon: TrendingUp,
      title: 'Efficiency',
      description: 'We optimize every system for maximum output and fastest ROI.'
    },
    {
      icon: Leaf,
      title: 'Sustainability',
      description: 'We are committed to creating a cleaner, greener future for generations to come.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <section className="py-20 md:py-32" data-testid="about-hero">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-7xl font-heading font-semibold text-primary tracking-tight leading-[1.1] mb-6">
              Powering India's Clean Energy Revolution
            </h1>
            <p className="text-lg md:text-xl text-stone-600 leading-relaxed">
              Amigos Renewable Energy is on a mission to make solar power accessible, affordable, and hassle-free for every Indian household and business. Creating Energy That Cares.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-stone-50" data-testid="about-story">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary tracking-tight mb-6">Our Story</h2>
            <div className="space-y-6 text-stone-600 text-lg leading-relaxed">
              <p>
                Founded with the goal of delivering reliable and efficient solar solutions, Amigos Renewable Energy recently completed this project to help reduce electricity costs while promoting sustainable energy adoption.
              </p>
              <p>
                This installation reflects our continued focus on quality execution, transparent processes, and long-term performance. By implementing a customized solar system, the project enables significant energy savings and contributes to lowering carbon emissions.
              </p>
              <p>
                Through thoughtful design, modern technology, and expert installation, we ensure that every project we deliver moves our clients one step closer to energy independence.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20" data-testid="about-mission-vision">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl p-8 border border-stone-100 shadow-card"
            >
              <Target className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-2xl font-heading font-semibold text-primary mb-4">Our Mission</h3>
              <p className="text-stone-600 text-lg leading-relaxed">
                To empower every household and business with clean, reliable, and affordable solar energy, driving India towards energy independence and environmental sustainability.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl p-8 border border-stone-100 shadow-card"
            >
              <Eye className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-2xl font-heading font-semibold text-primary mb-4">Our Vision</h3>
              <p className="text-stone-600 text-lg leading-relaxed">
                To become India's most trusted solar energy partner, known for excellence, innovation, and unwavering commitment to customer success and environmental responsibility.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-stone-50" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary tracking-tight mb-4">
              Our Core Values
            </h2>
            <p className="text-lg md:text-xl text-stone-600 max-w-3xl mx-auto">
              The principles that guide everything we do.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-3xl p-8 border border-stone-100 shadow-card text-center"
              >
                <value.icon className="w-12 h-12 text-accent mx-auto mb-4" />
                <h3 className="text-xl font-heading font-semibold text-primary mb-3">{value.title}</h3>
                <p className="text-stone-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};